<!-- src/components/marketplace/ToolCard.vue -->
<template>
  <div class="tool-card p-4 border rounded-lg hover:shadow-md flex flex-col justify-between bg-white/70 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
    <div>
      <h3 class="text-lg font-semibold mb-2">
        <component
          :is="routeTo ? 'router-link' : 'span'"
          :to="routeTo || undefined"
          class="text-blue-600 hover:underline"
        >
          {{ tool.name }}
        </component>
      </h3>

      <p class="text-sm text-gray-700 dark:text-gray-300">
        {{ tool.description }}
      </p>
    </div>

    <div class="mt-4">
      <BaseButton
        :variant="disabled || loading ? 'secondary' : 'primary'"
        class="w-full"
        :disabled="disabled || loading"
        @click="handleLaunch"
      >
        <span v-if="loading">Launching…</span>
        <span v-else>
          <!-- {{ disabled ? 'Unavailable' : `Launch (${displayCost})` }} -->
          {{ disabled ? 'Unavailable' : 'Launch' }}
        </span>
      </BaseButton>

      <p v-if="disabled" class="mt-2 text-xs text-amber-700 dark:text-amber-400">
        Not enough credits to launch this tool.
      </p>
      <p v-if="error" class="mt-2 text-xs text-red-600">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useAuthStore } from '@/stores/auth';
import { useMarketplaceStore } from '@/stores/marketplace';
import { useCashflowSimStore } from '@/modules/cashflow-simulator/store.js';


export default {
  name: 'ToolCard',
  components: { BaseButton },
  props: {
    tool: { type: Object, required: true }
  },
  emits: ['launch', 'error'],
  setup(props, { emit }) {
    const router = useRouter();
    const auth = useAuthStore();
    const mp = useMarketplaceStore();           // must expose launchTool(toolId) that deducts credits + logs usage
    const cf = useCashflowSimStore();           // for launching simulator run

    const loading = ref(false);
    const error = ref('');

    // Prefer explicit launchRoute; fallback to /tool/:id; else no link
    const routeTo = computed(() => {
      if (props.tool.launchRoute) return props.tool.launchRoute;
      if (props.tool.id) return `/tool/${props.tool.id}`;
      return '';
    });

    const isPaid = computed(() => props.tool.isPaid === true || props.tool.is_paid === true);
    const cost = computed(() => Number(props.tool.cost || 0));
    const credits = computed(() => Number(auth.credits || 0));

    // Disable only when tool is paid AND user lacks credits
    const disabled = computed(() => isPaid.value && cost.value > credits.value);

    const displayCost = computed(() => `${cost.value} cr`);

    function isCashflowTool() {
      const id = String(props.tool.id || '');
      const slug = String(props.tool.slug || '');
      const lr = String(props.tool.launchRoute || '');
      return (
        id === 'cashflow-simulator' ||
        slug === 'cashflow-simulator' ||
        lr.includes('/cashflow-simulator')
      );
    }

    function targetCashflowUploadRoute() {
      const base = props.tool.launchRoute || '/tool/cashflow-simulator';
      return `${base.replace(/\/$/, '')}/upload`;
    }

    async function handleLaunch() {
      error.value = '';
      if (disabled.value || loading.value) return;
      loading.value = true;

      try {
        // 1) Deduct credits + create usage log (Marketplace flow)
        //    Expectation: returns at least { usageLogId?, ... }
        const usage = await mp.launchTool(props.tool.id);
        if (usage?.usageLogId) cf.setUsageLogId(usage.usageLogId);

        // 2) Launch tool-specific flow
        if (isCashflowTool()) {
          // Launch a new simulator run, then go to Upload step
          await cf.doLaunch({ openingCash: 0, horizonMonths: 12 });
          await router.push(targetCashflowUploadRoute());
        } else if (props.tool.launchRoute) {
          // For other tools, just navigate to the declared route
          await router.push(props.tool.launchRoute);
        } else if (props.tool.id) {
          await router.push(`/tool/${props.tool.id}`);
        }

        // 3) Emit for parent listeners/telemetry if needed
        emit('launch', { tool: props.tool, usage });
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || 'Failed to launch tool';
        error.value = msg;
        emit('error', msg);
        // Optional: global toast if you have one
        // window?.$toast?.error?.(msg);
      } finally {
        loading.value = false;
      }
    }

    return {
      routeTo,
      displayCost,
      disabled,
      loading,
      error,
      handleLaunch
    };
  }
};
</script>

<style scoped>
.tool-card {
  display: flex;
  flex-direction: column;
}
</style>
